import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  updateProfile as firebaseUpdateProfile
} from 'firebase/auth';
import { auth, googleProvider } from '../../../firebaseConfig';
import {
  createUserDocument,
  getUserData
} from '../../../pages/auth/utils/firebase/userOperations';
import { showErrorToast, showSuccessToast, showInfoToast } from '../../../utils/toastUtils';
import { normalizeUserProfile } from '../../../utils/userDisplay.js';
import { getErrorMessage } from './utils.js';

// Register user with email/password
export const registerUser = createAsyncThunk(
  'auth/register',
  async ({ email, password, name }, { rejectWithValue }) => {
    try {
      showInfoToast('Creating your account...');

      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const { user } = userCredential;

      // Update user profile with display name
      await firebaseUpdateProfile(user, { displayName: name });

      // Create user document in Firestore
      const userData = {
        uid: user.uid,
        email: user.email,
        username: email.split('@')[0],
        name: name,
        displayName: name,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        emailVerified: false
      };

      await createUserDocument(user, userData);

      const completeUserData = await getUserData(user.uid);
      const normalized = normalizeUserProfile({ doc: { id: user.uid, ...completeUserData }, authUser: user });

      showSuccessToast('Account created successfully!');

      return { ...normalized, email: user.email, emailVerified: user.emailVerified };
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = getErrorMessage(error);
      showErrorToast(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Login user with email/password
export const loginUser = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      showInfoToast('Logging in...');

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const { user } = userCredential;

      const userData = await getUserData(user.uid);
      const normalized = normalizeUserProfile({ doc: { id: user.uid, ...userData }, authUser: user });

      if (!userData) {
        throw new Error('User data not found. Please try again.');
      }

      showSuccessToast(`Welcome back, ${userData.name || 'User'}!`);

      return { ...normalized, email: user.email, emailVerified: user.emailVerified };
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = getErrorMessage(error);
      showErrorToast(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Login with Google OAuth
export const loginWithGoogle = createAsyncThunk(
  'auth/loginWithGoogle',
  async (_, { rejectWithValue }) => {
    try {
      showInfoToast('Signing in with Google...');

      const result = await signInWithPopup(auth, googleProvider);
      const { user } = result;

      // Check if user is new
      const isNewUser = result._tokenResponse?.isNewUser;

      if (isNewUser) {
        // Create user document in Firestore for new users
        const userData = {
          uid: user.uid,
          email: user.email,
          name: user.displayName || user.email.split('@')[0],
          photoURL: user.photoURL || null,
          emailVerified: user.emailVerified,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        await createUserDocument(user, userData);
        showSuccessToast('Account created successfully!');
        const normalized = normalizeUserProfile({ doc: { id: user.uid, ...userData }, authUser: user });
        return { user, userData: normalized };
      }

      const userData = await getUserData(user.uid);
      const normalized = normalizeUserProfile({ doc: { id: user.uid, ...userData }, authUser: user });
      showSuccessToast(`Welcome back, ${userData?.name || 'User'}!`);

      return { user, userData: normalized };

    } catch (error) {
      console.error('Google login error:', error);
      const errorMessage = getErrorMessage(error);
      showErrorToast(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);
